import { useState, useCallback, useMemo } from 'react';
import axios from 'axios';
import { API_CONFIG } from '../config/authConfig';
import { PasswordResetState, PasswordResetFormData, PasswordResetAPI, UsePasswordResetResult } from '../types/passwordResetTypes';

const initialFormData: PasswordResetFormData = {
  login: '',
  iin: '',
  fullName: '',
  newPassword: '',
  confirmPassword: ''
};

const initialState: PasswordResetState = {
  currentStep: 1,
  formData: initialFormData,
  loading: false,
  error: '',
  showNewPassword: false,
  showConfirmPassword: false
};

// Реальный API для работы с восстановлением пароля
const realAPI: PasswordResetAPI = {
  checkUser: async (login: string) => {
    try {
      if (!login.trim()) {
        return { exists: false, message: 'Введите логин' };
      }

      const response = await axios.post(`${API_CONFIG.BASE_URL}/password-reset/check-username`, {
        username: login
      });

      if (response.data.success) {
        return { exists: true };
      } else {
        return { exists: false, message: response.data.message || 'Пользователь не найден' };
      }
    } catch (error: any) {
      console.error('Ошибка при проверке пользователя:', error);
      return { 
        exists: false, 
        message: error.response?.data?.message || 'Ошибка соединения с сервером' 
      };
    }
  },

  validateUserData: async (login: string, iin: string, fullName: string) => {
    try {
      if (!iin.trim() || !fullName.trim()) {
        return { valid: false, message: 'Заполните все поля' };
      }
      
      if (iin.length !== 12) {
        return { valid: false, message: 'ИИН должен содержать 12 цифр' };
      }

      const response = await axios.post(`${API_CONFIG.BASE_URL}/password-reset/verify-user-data`, {
        username: login,
        iin: iin,
        fullName: fullName
      });

      if (response.data.success) {
        return { valid: true };
      } else {
        return { valid: false, message: response.data.message || 'Данные не совпадают с учетной записью' };
      }
    } catch (error: any) {
      console.error('Ошибка при проверке данных пользователя:', error);
      return { 
        valid: false, 
        message: error.response?.data?.message || 'Ошибка соединения с сервером' 
      };
    }
  },

  resetPassword: async (login: string, newPassword: string) => {
    try {
      if (newPassword.length < 6) {
        return { success: false, message: 'Пароль должен содержать минимум 6 символов' };
      }

      // Проверка что пароль содержит только английские символы
      const englishPattern = /^[a-zA-Z0-9!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]*$/;
      if (!englishPattern.test(newPassword)) {
        return { success: false, message: 'Пароль должен содержать только английские символы' };
      }

      const response = await axios.post(`${API_CONFIG.BASE_URL}/password-reset/reset-password`, {
        username: login,
        newPassword: newPassword
      });

      if (response.data.success) {
        return { success: true };
      } else {
        return { success: false, message: response.data.message || 'Ошибка при сбросе пароля' };
      }
    } catch (error: any) {
      console.error('Ошибка при сбросе пароля:', error);
      return { 
        success: false, 
        message: error.response?.data?.message || 'Ошибка соединения с сервером' 
      };
    }
  }
};

export const usePasswordReset = (api: PasswordResetAPI = realAPI): UsePasswordResetResult => {
  const [state, setState] = useState<PasswordResetState>(initialState);

  // Setters для формы
  const setLogin = useCallback((login: string) => {
    setState(prev => ({
      ...prev,
      formData: { ...prev.formData, login },
      error: ''
    }));
  }, []);

  const setIin = useCallback((iin: string) => {
    setState(prev => ({
      ...prev,
      formData: { ...prev.formData, iin },
      error: ''
    }));
  }, []);

  const setFullName = useCallback((fullName: string) => {
    setState(prev => ({
      ...prev,
      formData: { ...prev.formData, fullName },
      error: ''
    }));
  }, []);

  const setNewPassword = useCallback((newPassword: string) => {
    setState(prev => ({
      ...prev,
      formData: { ...prev.formData, newPassword },
      error: ''
    }));
  }, []);

  const setConfirmPassword = useCallback((confirmPassword: string) => {
    setState(prev => ({
      ...prev,
      formData: { ...prev.formData, confirmPassword },
      error: ''
    }));
  }, []);

  const toggleShowNewPassword = useCallback(() => {
    setState(prev => ({
      ...prev,
      showNewPassword: !prev.showNewPassword
    }));
  }, []);

  const toggleShowConfirmPassword = useCallback(() => {
    setState(prev => ({
      ...prev,
      showConfirmPassword: !prev.showConfirmPassword
    }));
  }, []);

  // Валидация шагов
  const validation = useMemo(() => ({
    canProceedStep1: Boolean(state.formData.login.trim()),
    canProceedStep2: Boolean(state.formData.iin.trim() && state.formData.fullName.trim()),
    canProceedStep3: Boolean(
      state.formData.newPassword.trim() && 
      state.formData.confirmPassword.trim() &&
      state.formData.newPassword === state.formData.confirmPassword
    )
  }), [state.formData]);

  // Логика перехода к следующему шагу
  const nextStep = useCallback(async () => {
    setState(prev => ({ ...prev, loading: true, error: '' }));

    try {
      switch (state.currentStep) {
        case 1: {
          const result = await api.checkUser(state.formData.login);
          if (!result.exists) {
            setState(prev => ({ 
              ...prev, 
              loading: false, 
              error: result.message || 'Пользователь не найден' 
            }));
            return;
          }
          break;
        }

        case 2: {
          const result = await api.validateUserData(
            state.formData.login,
            state.formData.iin,
            state.formData.fullName
          );
          if (!result.valid) {
            setState(prev => ({ 
              ...prev, 
              loading: false, 
              error: result.message || 'Данные не совпадают с учетной записью' 
            }));
            return;
          }
          break;
        }

        case 3: {
          if (state.formData.newPassword !== state.formData.confirmPassword) {
            setState(prev => ({ 
              ...prev, 
              loading: false, 
              error: 'Пароли не совпадают' 
            }));
            return;
          }

          const result = await api.resetPassword(
            state.formData.login,
            state.formData.newPassword
          );
          if (!result.success) {
            setState(prev => ({ 
              ...prev, 
              loading: false, 
              error: result.message || 'Ошибка при сбросе пароля' 
            }));
            return;
          }
          break;
        }
      }

      // Переход к следующему шагу
      setState(prev => ({ 
        ...prev, 
        currentStep: prev.currentStep + 1, 
        loading: false 
      }));

    } catch (error) {
      setState(prev => ({ 
        ...prev, 
        loading: false, 
        error: 'Произошла ошибка. Попробуйте еще раз.' 
      }));
    }
  }, [api, state.currentStep, state.formData]);

  const prevStep = useCallback(() => {
    setState(prev => ({
      ...prev,
      currentStep: Math.max(1, prev.currentStep - 1),
      error: ''
    }));
  }, []);

  const reset = useCallback(() => {
    setState(initialState);
  }, []);

  return {
    state,
    actions: {
      setLogin,
      setIin,
      setFullName,
      setNewPassword,
      setConfirmPassword,
      toggleShowNewPassword,
      toggleShowConfirmPassword,
      nextStep,
      prevStep,
      reset
    },
    validation
  };
};
